import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  Card,
  Grid,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import { useParams } from 'react-router-dom';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { TaskAssignmentValues } from 'src/models/timesheet';
import ProjectSelect from './common/ProjectSelect';
import { useForm } from 'react-hook-form';
import TaskNameMultiSelect from './common/TaskNameMultiSelect';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import ResourcesMultiSelect from './common/ResourcesMultiSelect';
import { assignTasks } from 'src/services/timeManagementService';
import SingleDatePicker from './common/SingleDatePicker';
import moment from 'moment';
import EditIcon from '@mui/icons-material/Edit';
import ITimeEntry from './components/timeEntry';

interface TaskAssignProps {
  permissions: any;
  projectsList: any;
  setProjectValue: (data) => void;
  getTableData: () => void;
}

export interface TaskAssignRef {
  project: any;
}

const TaskAssign = forwardRef<TaskAssignRef, TaskAssignProps>(({ permissions, projectsList, setProjectValue, getTableData }, ref) => {
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);
  const dispatch = useDispatch();
  const { projectId, taskId, employeeId } = useParams();

  let initialValues = {
    resources: null,
    taskIds: null,
    assignmentStartDate: null,
    assignmentEndDate: null,
    planHours:null
  } as TaskAssignmentValues;

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [taskAssignmentValues, setTaskAssignmentValues] = useState(initialValues as TaskAssignmentValues);
  const [isDisabled, setIsDisabled] = useState(false);
  const [project, setProject] = useState<any>('');


  const [isTimeEntryActive, setIsTimeEntryActive] = useState(false);

  useEffect(() => {
    if (projectsList.length > 0) {
      if (projectId) {
        const matchedProject = projectsList.find(
          (p) => p.value?.projectId === projectId
        );
        if (matchedProject) {
          setProject(matchedProject.value);
          setProjectValue(matchedProject.value);
        } else {
          setProject(projectsList[0]?.value);
        }
      } else {
        setProject(projectsList[0]?.value);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsList, projectId]);

  const { register, handleSubmit, reset, setValue } =
    useForm<TaskAssignmentValues>({
      defaultValues: initialValues
    });

  const onSubmit = () => {
    dispatch(AppActions.showLoader());
    const payload = [];
    for(let i=0; i < taskAssignmentValues?.taskIds.length; i++){
      for(let j=0; j < taskAssignmentValues?.resources.length; j++){
        const data = {
          taskId: taskAssignmentValues?.taskIds[i],
          assigneeId: taskAssignmentValues?.resources[j].employeeId,
          status: "TO_DO",
          isAllocatedResource: taskAssignmentValues?.resources[j].isAllocatedResource,
          assignmentStartDate: taskAssignmentValues?.assignmentStartDate ? moment(taskAssignmentValues.assignmentStartDate).format('YYYY-MM-DD') : null,
          assignmentEndDate: taskAssignmentValues?.assignmentEndDate ? moment(taskAssignmentValues.assignmentEndDate).format('YYYY-MM-DD') : null,
          planHours: taskAssignmentValues?.planHours === '0h 0m' ? '' : taskAssignmentValues?.planHours,
        }
        payload.push(data);
      }
    }

    assignTasks(payload).then((res) => {
      if (res?.data?.statusCode === 403){
        setSnackBarMessage(res?.data?.message);
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      } else {
        getTableData();
        resetFilterResult();

        setSnackBarMessage(res?.data?.message ? res?.data?.message : 'You have assigned tasks successfully.');
        setSnackbarSeverity('success');
        setSnackBarOpen(true);
      }
      dispatch(AppActions.hideLoader());
    })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable to assign tasks');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
  };

  //clear the filter
  const resetFilterResult = () => {
    reset();
    setTaskAssignmentValues(initialValues);
  };

  useEffect(() => {
    const newValue = {
      resources: taskAssignmentValues?.resources,
      taskIds: taskAssignmentValues?.taskIds,
      assignmentStartDate: taskAssignmentValues?.assignmentStartDate
    };

    const isNullish = Object.values(newValue).some(value => value === null);
    if (isNullish) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [taskAssignmentValues]);


  useImperativeHandle(ref, () => ({
    project
  }));
  // Function to handle active status change
  const handleIsActiveChange = (isActive) => {
    setIsTimeEntryActive(isActive);
  };

  const handleEditClick = () => {
    setIsTimeEntryActive(true);
  }

  return (
    <div className='task-assign' style={{ marginBottom: 10 }}>
      <Card>
        <Grid>
          <Grid
            container
            style={{
              justifyContent: 'between',
              flexWrap: 'nowrap',
              alignItems: 'center'
            }}
          >
            <Grid style={{
                width: '33.4%',
              paddingRight:'4px',
              paddingBottom:'10px'
            }} sx={{ px: 2, py: 2 }} item display='flex'>
              <ProjectSelect
                width='100%'
                list={projectsList}
                value={project || ''}
                onChange={(e: any) => {
                  resetFilterResult();
                  setProject(e.target.value);
                  setProjectValue(e.target.value);
                }} />
            </Grid>
            <Grid item
              style={{
                width: '33.3%',
              }}
            >
            </Grid>
            <Grid item
              style={{
                width: '33.3%',
              }}
            >
            </Grid>
          </Grid>
        </Grid>
        <Grid px={2} pb={2} style={{ margin: 0 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
              container
              spacing={1}
              style={{
                justifyContent: 'between',
                flexWrap: 'nowrap',
                alignItems: 'center'
              }}
            >
              <Grid item
                style={{
                  width: '33.3%',
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              >
                <TaskNameMultiSelect
                  {...register('taskIds')}
                  titile='Activities'
                  disabled={project?.isClosed}
                  value={taskAssignmentValues?.taskIds || []}
                  projectId={project?.projectId}
                  taskView={true}
                  onChange={(e: any) => {
                    setTaskAssignmentValues((prev) => ({
                      ...prev,
                      taskIds: e.target.value
                    }));
                    setValue('taskIds', e.target.value);
                  }} />  
              </Grid>
              <Grid style={{
                width: '33.3%',
                flexGrow: 0,
                flexShrink: 0,
              }} item>
                <ResourcesMultiSelect
                  titile='Resources'
                  disabled={project?.isClosed}
                  {...register('resources')}
                  getAll={true}
                  value={taskAssignmentValues?.resources || []}
                  projectId={project?.key}
                  onChange={(e: any) => {
                    let newValue = e?.target?.value;
                    const newResource = newValue[newValue?.length - 1];
                    let isExsit = taskAssignmentValues?.resources?.map(e => e.employeeId).includes(newResource.employeeId);

                    if (isExsit) {
                      newValue = newValue.filter(e => e.employeeId !== newResource.employeeId);
                    }

                    setTaskAssignmentValues((prev) => ({
                      ...prev,
                      resources: newValue
                    }));
                    setValue('resources', newValue);
                  }} />
              </Grid>

              <Grid item style={{
                width: '33.3%',
                flexGrow: 0,
                flexShrink: 0,
              }} >

                {!isTimeEntryActive ? (
                  <TextField
                    {...register('planHours')}
                    size="small"
                    label="Planned Hours"
                    placeholder="Planned Hours"
                    fullWidth
                    value={taskAssignmentValues?.planHours || ''}
                    style={{
                      fontSize: '12px',
                      height: '2rem'
                    }}
                    InputProps={{
                      readOnly: true,
                      style: {
                        height: '2rem',
                        fontSize: '12px',
                        marginTop: 4,
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <EditIcon
                            sx={{
                              fontSize: '16px',
                            }}
                            onClick={handleEditClick}
                          />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{
                      style: {
                        fontSize: '12px',
                        marginTop: 4
                      },
                    }}
                    onFocus={(event) => {
                      event.target.select();
                    }}
                    disabled={true}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled::placeholder': {
                        opacity: 0.5, 
                        color: 'rgba(0, 0, 0, 0.6)', 
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                        color: 'rgba(0, 0, 0, 0.87)', 
                        opacity: 1, 
                      },
                      '& .Mui-disabled': {
                        backgroundColor: 'inherit',
                      },
                    }}
                  />) : (
                  <ITimeEntry
                    time={taskAssignmentValues?.planHours || null}
                    onTimeChange={(updatedTime) => {
                      setTaskAssignmentValues((prev) => ({
                        ...prev,
                        planHours: updatedTime
                      }));
                      setValue('planHours', updatedTime);
                    }}
                    onIsActiveChange={handleIsActiveChange}
                  />

                )
                }
              </Grid>
            </Grid>
            <Grid
              container
              spacing={1}
              style={{
                justifyContent: 'between',
                flexWrap: 'nowrap',
                alignItems: 'center',
                marginTop: '5px'
              }}
            >
              <Grid item
                style={{
                  width: 'calc((100% - 135px)/2)',
                }}>
                <SingleDatePicker
                  {...register('assignmentStartDate')}
                  fullWidth={true}
                  label="Assignment Start Date"
                  value={taskAssignmentValues?.assignmentStartDate || null}
                  onChange={(e: any) => {
                    setTaskAssignmentValues((prev) => ({
                      ...prev,
                      assignmentStartDate: e
                    }));
                    setValue('assignmentStartDate', e);
                    // Reset end date if it's less than the new start date
                    if (taskAssignmentValues.assignmentEndDate && e > taskAssignmentValues.assignmentEndDate) {
                      setTaskAssignmentValues((prev) => ({
                        ...prev,
                        assignmentEndDate: null,
                      }));
                      setValue('assignmentEndDate', null);
                    }
                  }}
                />
              </Grid>
              <Grid item 
                style={{
                  width: 'calc((100% - 135px)/2)',
                }} >
                <SingleDatePicker
                  {...register('assignmentEndDate')}
                  fullWidth={true}
                  label="Assignment End Date"
                  value={taskAssignmentValues?.assignmentEndDate || null}
                  onChange={(e: any) => {
                    // Check if end date is after start date
                    if (e < taskAssignmentValues.assignmentStartDate) {
                      setSnackBarMessage('End date cannot be earlier than start date.');
                      setSnackbarSeverity('warning');
                      setSnackBarOpen(true);
                      return;
                    }
                    setTaskAssignmentValues((prev) => ({
                      ...prev,
                      assignmentEndDate: e
                    }));
                    setValue('assignmentEndDate', e);
                  }}
                />
              </Grid>
              <Grid item
                style={{
                  justifyContent: 'end',
                  alignItems: 'center',
                  display: 'flex',
                  width: 'calc((100% - 135px)/2)',
                }}>
                <Button
                  sx={{ mt: { xs: 2, md: 0 } }}
                  id="btn-onSubmit"
                  variant="contained"
                  type="submit"
                  disabled={isDisabled || project?.isClosed || isTimeEntryActive}
                  className={(isDisabled || project?.isClosed) && 'filter-btn'}
                  style={{ height: '2rem', fontSize: '12px', width: '130px' }}
                >
                  + Assign Tasks
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Card>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </div>
  );
});

export default TaskAssign;
